import React, { useEffect, useState } from 'react';
import Seo from '../components/Seo';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';

// Simple frontmatter parser (no Buffer needed)
const parseFrontmatter = (text) => {
  const frontmatterRegex = /^---\s*\n([\s\S]*?)\n---\s*\n([\s\S]*)$/;
  const match = text.match(frontmatterRegex);

  if (!match) {
    return { data: {}, content: text };
  }

  const frontmatterText = match[1];
  const content = match[2];

  const data = {};
  frontmatterText.split('\n').forEach(line => {
    const colonIndex = line.indexOf(':');
    if (colonIndex > 0) {
      const key = line.substring(0, colonIndex).trim();
      let value = line.substring(colonIndex + 1).trim();

      // Parse arrays
      if (value.startsWith('[') && value.endsWith(']')) {
        value = value.slice(1, -1).split(',').map(v => v.trim());
      }

      data[key] = value;
    }
  });

  return { data, content };
};

const BlogPost = () => {
  const { slug } = useParams();
  const [content, setContent] = useState('');
  const [frontmatter, setFrontmatter] = useState({});
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [readingProgress, setReadingProgress] = useState(0);

  useEffect(() => {
    const fetchBlogPost = async () => {
      try {
        const response = await fetch(`/blog/${slug}.md`);
        if (!response.ok) {
          throw new Error('Blog post not found');
        }
        const text = await response.text();

        const { data, content: markdown } = parseFrontmatter(text);
        setFrontmatter(data);
        setContent(markdown);

        // Cross-links: 3 sibling posts sharing the most tags (SEO audit
        // Phase 2 — every post links siblings + the /blog hub).
        try {
          const idx = await (await fetch('/blog/blogIndex.json')).json();
          const posts = (Array.isArray(idx) ? idx : idx.posts || []).filter((p) => p.slug !== slug);
          const myTags = new Set(((data.tags || '').toString().replace(/[\[\]]/g, '').split(',')).map((t) => t.trim().toLowerCase()));
          const scored = posts
            .map((p) => ({ p, score: (p.tags || []).filter((t) => myTags.has(t.toLowerCase())).length }))
            .sort((a, b) => b.score - a.score);
          setRelated(scored.slice(0, 3).map((x) => x.p));
        } catch { /* cross-links are progressive enhancement */ }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogPost();
  }, [slug]);

  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY;
      const progress = (scrollTop / (documentHeight - windowHeight)) * 100;
      setReadingProgress(Math.min(progress, 100));
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const getReadingTime = () => {
    // Use readingTime from frontmatter if available
    if (frontmatter.readingTime) {
      return frontmatter.readingTime;
    }
    // Otherwise calculate from content
    const wordsPerMinute = 200;
    const words = content.split(' ').length;
    return Math.ceil(words / wordsPerMinute);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-3 border-gray-200 border-t-[#0065F8] mb-4"></div>
          <p className="text-gray-500 text-sm">Loading article...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white">
        {/* Clear the fixed site navbar (same clearance as the article view) */}
        <div className="container mx-auto px-6 pb-12 pt-28 sm:pt-32 max-w-3xl">
          <div className="bg-red-50 border border-red-200 rounded-lg p-12 text-center">
            <h1 className="text-3xl font-bold text-red-600 mb-4">Article Not Found</h1>
            <p className="text-gray-600 mb-8">{error}</p>
            <Link
              to="/blog"
              className="inline-flex items-center px-6 py-3 bg-[#0065F8] hover:bg-[#0055D6] text-white rounded-full transition-colors font-bold"
              style={{ fontFamily: "'Fredoka', sans-serif", letterSpacing: "0.04em", textTransform: "uppercase", fontSize: 14, boxShadow: "0px 4px 0px #073B8E" }}
            >
              ← Back to Blog
            </Link>
          </div>
        </div>
      </div>
    );
  }

  let faqs = [];
  try {
    faqs = frontmatter.faqs ? JSON.parse(frontmatter.faqs) : [];
  } catch { faqs = []; }

  return (
    <div className="min-h-screen bg-white" style={{fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'}}>
      {frontmatter.title && (
        <Seo
          title={`${frontmatter.title} | Pixley Blog`}
          description={frontmatter.description || ''}
          path={`/blog/${slug}`}
          type="article"
          image={frontmatter.coverImage ? `https://www.pixleyai.com${frontmatter.coverImage}` : undefined}
          jsonLd={[
            {
              "@context": "https://schema.org",
              "@type": "Article",
              headline: frontmatter.title,
              description: frontmatter.description || '',
              datePublished: frontmatter.date || undefined,
              dateModified: frontmatter.updated || frontmatter.date || undefined,
              author: { "@type": "Organization", name: frontmatter.author || "Pixley Team" },
              publisher: { "@type": "Organization", name: "Pixley", logo: { "@type": "ImageObject", url: "https://www.pixleyai.com/assets/pixley-logo-full.png" } },
              mainEntityOfPage: `https://www.pixleyai.com/blog/${slug}`,
            },
            ...(faqs.length
              ? [{
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  mainEntity: faqs.map((f) => ({
                    "@type": "Question",
                    name: f.q,
                    acceptedAnswer: { "@type": "Answer", text: f.a },
                  })),
                }]
              : []),
          ]}
        />
      )}
      {/* "All articles" bar — sits just below the floating site navbar, with
          the reading-progress line running along its bottom edge */}
      <nav className="sticky bg-white/95 backdrop-blur-sm border-b border-gray-200 z-30 mt-24 sm:mt-28" style={{ top: 92 }}>
        <div className="container mx-auto px-6 py-4 max-w-3xl">
          <Link
            to="/blog"
            className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors font-medium text-sm"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            All Articles
          </Link>
        </div>
        {/* Reading progress bar — directly under the All Articles bar */}
        <div className="absolute left-0 right-0 bottom-0 h-1 bg-gray-100">
          <div
            className="h-full bg-[#0065F8] transition-all duration-150"
            style={{ width: `${readingProgress}%` }}
          ></div>
        </div>
      </nav>

      {/* Article */}
      <article className="container mx-auto px-6 py-12 md:py-16 max-w-3xl">
        {/* Header */}
        <header className="mb-12 pb-12 border-b border-gray-200">
          {frontmatter.headerImage && (
            <div className="mb-8 -mx-6 md:-mx-0 rounded-none md:rounded-lg overflow-hidden">
              <img
                src={frontmatter.headerImage}
                alt={frontmatter.title}
                className="w-full h-auto object-cover"
              />
            </div>
          )}
          <div className="mb-8">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-[1.1] tracking-tight">
              {frontmatter.title}
            </h1>

            {frontmatter.description && (
              <p className="text-xl md:text-2xl text-gray-600 leading-relaxed">
                {frontmatter.description}
              </p>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
            {frontmatter.author && (
              <div className="flex items-center gap-2">
                <img
                  src="/assets/pixley-panda-nobg.png"
                  alt="Pixley"
                  className="w-6 h-6 rounded-full object-cover"
                />
                <span className="font-medium text-gray-900">{frontmatter.author}</span>
              </div>
            )}

            {frontmatter.date && (
              <>
                <span className="text-gray-400">·</span>
                <time>
                  {/* +T00:00:00 forces local-time parsing — see BlogList */}
                  {new Date(frontmatter.date + 'T00:00:00').toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </time>
              </>
            )}
            {frontmatter.updated && frontmatter.updated !== frontmatter.date && (
              <>
                <span className="text-gray-400">·</span>
                <span className="text-gray-600">
                  Updated {new Date(frontmatter.updated + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </span>
              </>
            )}

            {content && (
              <>
                <span className="text-gray-400">·</span>
                <span>{getReadingTime()} min read</span>
              </>
            )}
          </div>

          {frontmatter.tags && frontmatter.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-6">
              {frontmatter.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </header>

        {/* Content */}
        <div className="prose prose-lg max-w-none">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={{
              code({ node, inline, className, children, ...props }) {
                const match = /language-(\w+)/.exec(className || '');
                return !inline && match ? (
                  <div className="my-8 rounded-lg overflow-hidden border border-gray-200">
                    <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                      <span className="text-xs text-gray-600 font-mono uppercase">{match[1]}</span>
                    </div>
                    <SyntaxHighlighter
                      style={oneLight}
                      language={match[1]}
                      PreTag="div"
                      customStyle={{
                        margin: 0,
                        padding: '1.5rem',
                        fontSize: '0.875rem',
                        lineHeight: '1.7',
                        backgroundColor: '#fafafa'
                      }}
                      {...props}
                    >
                      {String(children).replace(/\n$/, '')}
                    </SyntaxHighlighter>
                  </div>
                ) : (
                  <code className="px-1.5 py-0.5 bg-gray-100 text-gray-800 rounded text-sm font-mono" {...props}>
                    {children}
                  </code>
                );
              },
              h1: ({ node, ...props }) => (
                <h1 className="text-4xl font-bold text-gray-900 mt-12 mb-6 leading-tight" {...props} />
              ),
              h2: ({ node, ...props }) => (
                <h2 className="text-3xl font-bold text-gray-900 mt-10 mb-5 leading-tight" {...props} />
              ),
              h3: ({ node, ...props }) => (
                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4 leading-tight" {...props} />
              ),
              h4: ({ node, ...props }) => (
                <h4 className="text-xl font-bold text-gray-900 mt-6 mb-3" {...props} />
              ),
              p: ({ node, ...props }) => (
                <p className="text-gray-700 text-lg leading-relaxed mb-6" {...props} />
              ),
              a: ({ node, ...props }) => (
                <a
                  className="text-[#0065F8] hover:text-[#0055D6] underline decoration-2 underline-offset-2 font-medium transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                  {...props}
                />
              ),
              ul: ({ node, ...props }) => (
                <ul className="space-y-2 mb-6 ml-6 list-disc marker:text-gray-400" {...props} />
              ),
              ol: ({ node, ...props }) => (
                <ol className="space-y-2 mb-6 ml-6 list-decimal marker:text-gray-400" {...props} />
              ),
              li: ({ node, ...props }) => (
                <li className="text-gray-700 text-lg leading-relaxed pl-2" {...props} />
              ),
              blockquote: ({ node, ...props }) => (
                <blockquote className="border-l-4 border-[#0065F8] pl-6 py-4 my-8 bg-[#EAF3FF] rounded-r-lg italic text-[#0B1A33] text-lg font-medium" {...props} />
              ),
              img: ({ node, ...props }) => {
                // Check if this is the infographic based on alt text
                const isInfographic = props.alt && (props.alt.includes('10 Benefits of Interactive Stories') || props.alt.toLowerCase().includes('infographic'));
                return (
                  <img
                    className={`rounded-lg my-8 shadow-md mx-auto ${isInfographic ? 'w-1/2' : 'w-full'}`}
                    {...props}
                    alt={props.alt || ''}
                  />
                );
              },
              hr: ({ node, ...props }) => (
                <hr className="my-12 border-gray-200" {...props} />
              ),
              strong: ({ node, ...props }) => (
                <strong className="font-bold text-gray-900" {...props} />
              ),
              em: ({ node, ...props }) => (
                <em className="italic" {...props} />
              ),
              table: ({ node, ...props }) => (
                <div className="my-8 overflow-x-auto rounded-lg border border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200" {...props} />
                </div>
              ),
              thead: ({ node, ...props }) => (
                <thead className="bg-gray-50" {...props} />
              ),
              tbody: ({ node, ...props }) => (
                <tbody className="bg-white divide-y divide-gray-200" {...props} />
              ),
              tr: ({ node, ...props }) => (
                <tr className="hover:bg-gray-50 transition-colors" {...props} />
              ),
              th: ({ node, ...props }) => (
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider" {...props} />
              ),
              td: ({ node, ...props }) => (
                <td className="px-6 py-4 text-sm text-gray-700" {...props} />
              ),
            }}
          >
            {content}
          </ReactMarkdown>
        </div>

        {/* FAQ — same questions as the FAQPage JSON-LD, visible on page */}
        {faqs.length > 0 && (
          <section className="mt-14" aria-labelledby="post-faq">
            <h2 id="post-faq" className="text-2xl font-bold mb-6" style={{ fontFamily: "'Fredoka', sans-serif", color: '#0B1A33' }}>
              Frequently Asked Questions
            </h2>
            <div className="space-y-5">
              {faqs.map((f, i) => (
                <div key={i} className="rounded-2xl border border-gray-200 p-5" style={{ background: '#F8FBFF' }}>
                  <h3 className="font-bold mb-2" style={{ fontFamily: "'Fredoka', sans-serif", color: '#0B1A33', fontSize: 17 }}>{f.q}</h3>
                  <p className="text-gray-700 leading-relaxed" style={{ fontSize: 15 }}>{f.a}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Related posts — descriptive-anchor cross-links */}
        {related.length > 0 && (
          <section className="mt-14" aria-labelledby="post-related">
            <h2 id="post-related" className="text-2xl font-bold mb-6" style={{ fontFamily: "'Fredoka', sans-serif", color: '#0B1A33' }}>
              Keep Reading
            </h2>
            <ul className="space-y-3">
              {related.map((p) => (
                <li key={p.slug}>
                  <Link to={`/blog/${p.slug}`} className="font-semibold hover:underline" style={{ color: '#0065F8' }}>
                    {p.title}
                  </Link>
                  {p.description && <p className="text-gray-600 text-sm mt-1">{p.description}</p>}
                </li>
              ))}
              <li>
                <Link to="/blog" className="font-semibold hover:underline" style={{ color: '#0065F8' }}>
                  All parenting & screen-time guides →
                </Link>
              </li>
            </ul>
          </section>
        )}

        {/* Footer */}
        <div className="mt-16 pt-12 border-t border-gray-200 flex items-center justify-between">
          <Link
            to="/blog"
            className="inline-flex items-center transition-all duration-150"
            style={{
              fontFamily: "'Fredoka', sans-serif",
              background: "linear-gradient(180deg, #0A7DFF 0%, #0065F8 100%)",
              color: "#FFFFFF",
              fontWeight: 800,
              fontSize: 14,
              padding: "12px 24px",
              borderRadius: 999,
              border: "none",
              boxShadow: "0px 4px 0px #073B8E",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
            }}
          >
            ← Back to All Articles
          </Link>
          <a
            href="https://pixleyai.com"
            className="inline-flex items-center gap-2 transition-all duration-150"
            style={{
              fontFamily: "'Fredoka', sans-serif",
              background: "#FFFFFF",
              border: "2px solid rgba(0,101,248,0.18)",
              color: "#0065F8",
              fontWeight: 800,
              fontSize: 13,
              padding: "10px 18px",
              borderRadius: 999,
              boxShadow: "0px 3px 0px rgba(11,26,51,0.06)",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
            }}
          >
            <img src="/assets/pixley-panda-nobg.png" alt="Pixley" className="w-5 h-5" />
            Try Pixley Free
          </a>
        </div>
      </article>
    </div>
  );
};

export default BlogPost;
